import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { 
  Clock, MapPin, Calendar, Bell, FileText, 
  ChevronRight, Play, Square, AlertCircle, CheckCircle,
  Home, Users, BookOpen, LogOut, Phone, MessageSquare,
  Navigation, AlertTriangle
} from "lucide-react";
import RunningLateModal from "../../components/employee/RunningLateModal";
import NeedAssistanceModal from "../../components/employee/NeedAssistanceModal";
import MessageOfficeModal from "../../components/employee/MessageOfficeModal";
import NextVisitCard from "../../components/employee/NextVisitCard";

export default function CaregiverDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clockingIn, setClockingIn] = useState(false);
  const [clockingOut, setClockingOut] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const [officeContact, setOfficeContact] = useState(null);
  
  // Modal states
  const [showRunningLate, setShowRunningLate] = useState(false);
  const [showNeedAssistance, setShowNeedAssistance] = useState(false);
  const [showMessageOffice, setShowMessageOffice] = useState(false);
  const [justClockedOut, setJustClockedOut] = useState(false);

  useEffect(() => {
    loadDashboard();
    loadOfficeContact();
  }, []);

  const loadDashboard = async () => {
    try {
      const { data } = await api.get("/employee/dashboard");
      setDashboard(data);
    } catch (err) {
      console.error("Failed to load dashboard:", err);
    } finally {
      setLoading(false);
    }
  };
  
  const loadOfficeContact = async () => {
    try {
      const { data } = await api.get("/evv/office-contact");
      setOfficeContact(data);
    } catch (err) {
      console.warn("Could not load office contact:", err);
    }
  };

  const getCurrentPosition = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation not supported"));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy_meters: pos.coords.accuracy,
        }),
        (err) => reject(err),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  };

  const handleClockIn = async (clientId) => {
    setClockingIn(true);
    setGpsError(null);
    
    try {
      const gps = await getCurrentPosition();
      const { data } = await api.post("/employee/clock-in", {
        client_id: clientId,
        gps,
        device_info: navigator.userAgent,
      });
      
      // Refresh dashboard
      await loadDashboard();
      setSelectedClient(null);
    } catch (err) {
      if (err.code === 1) {
        setGpsError("Location access denied. Please enable GPS.");
      } else if (err.code === 2) {
        setGpsError("Location unavailable. Please try again.");
      } else {
        setGpsError(err.response?.data?.detail || "Clock in failed");
      }
    } finally {
      setClockingIn(false);
    }
  };

  const handleClockOut = async () => {
    if (!dashboard?.active_visit) return;
    
    setClockingOut(true);
    setGpsError(null);
    
    try {
      const gps = await getCurrentPosition();
      await api.post("/employee/clock-out", {
        visit_id: dashboard.active_visit.id,
        gps,
        device_info: navigator.userAgent,
      });
      
      // Show "Next Visit" card after clock out
      setJustClockedOut(true);
      
      // Refresh dashboard
      await loadDashboard();
    } catch (err) {
      setGpsError(err.response?.data?.detail || "Clock out failed");
    } finally {
      setClockingOut(false);
    }
  };
  
  const callOffice = () => {
    if (officeContact?.office_phone) {
      window.location.href = `tel:${officeContact.office_phone}`;
    } else if (officeContact?.on_call_phone) {
      window.location.href = `tel:${officeContact.on_call_phone}`;
    }
  };
  
  const openMaps = (address) => {
    if (address) {
      const encodedAddress = encodeURIComponent(address);
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, "_blank");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/employee");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  const activeVisit = dashboard?.active_visit;
  const assignedClients = dashboard?.assigned_clients || [];
  const unreadNotifications = dashboard?.unread_notifications || 0;

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-[#06372B] text-white px-4 py-5 sticky top-0 z-40">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <div>
            <div className="text-sm text-white/70">Welcome back</div>
            <div className="font-semibold text-lg">{user?.name || "Caregiver"}</div>
          </div>
          <div className="flex items-center gap-3">
            <Link 
              to="/employee/notifications" 
              className="relative p-2 rounded-full hover:bg-white/10 transition-colors"
              data-testid="notifications-link"
            >
              <Bell className="w-5 h-5" />
              {unreadNotifications > 0 && (
                <span className="absolute top-0 right-0 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center font-bold">
                  {unreadNotifications > 9 ? "9+" : unreadNotifications}
                </span>
              )}
            </Link>
            <button 
              onClick={handleLogout}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-5 space-y-5">
        {/* GPS Error Alert */}
        {gpsError && (
          <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700">
            <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
            <div>
              <div className="font-medium">Location Error</div>
              <div className="text-sm mt-0.5">{gpsError}</div>
            </div>
          </div>
        )}

        {/* Active Visit Card */}
        {activeVisit && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-testid="active-visit-card">
            <div className="bg-emerald-50 px-4 py-3 border-b border-emerald-100">
              <div className="flex items-center gap-2 text-emerald-700 font-medium">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                Currently Clocked In
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold text-lg">{activeVisit.client_name}</div>
                  <div className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Started {new Date(activeVisit.clock_in?.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              </div>
              
              {/* Quick Actions for Active Visit */}
              <div className="mt-4 grid grid-cols-2 gap-2">
                <Link
                  to={`/employee/visit/${activeVisit.id}`}
                  className="py-3 px-4 rounded-xl bg-[#06372B] text-white font-medium text-center text-sm"
                  data-testid="view-visit-btn"
                >
                  Visit Details
                </Link>
                <button
                  onClick={handleClockOut}
                  disabled={clockingOut}
                  className="py-3 px-4 rounded-xl bg-red-50 text-red-700 font-medium flex items-center justify-center gap-2 hover:bg-red-100 transition-colors disabled:opacity-60 text-sm"
                  data-testid="clock-out-btn"
                >
                  <Square className="w-4 h-4" />
                  {clockingOut ? "..." : "Clock Out"}
                </button>
              </div>
              
              {/* Action Buttons Row */}
              <div className="mt-3 flex gap-2">
                {activeVisit.client?.address && (
                  <button
                    onClick={() => openMaps(activeVisit.client?.address)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl bg-blue-50 text-blue-700 font-medium text-sm hover:bg-blue-100 transition-colors"
                    data-testid="navigate-btn"
                  >
                    <Navigation className="w-4 h-4" />
                    Navigate
                  </button>
                )}
                {officeContact?.office_phone && (
                  <button
                    onClick={callOffice}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl bg-gray-100 text-gray-700 font-medium text-sm hover:bg-gray-200 transition-colors"
                    data-testid="call-office-btn"
                  >
                    <Phone className="w-4 h-4" />
                    Call Office
                  </button>
                )}
                <button
                  onClick={() => setShowMessageOffice(true)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl bg-gray-100 text-gray-700 font-medium text-sm hover:bg-gray-200 transition-colors"
                  data-testid="message-office-btn"
                >
                  <MessageSquare className="w-4 h-4" />
                  Message
                </button>
              </div>
              
              {/* Need Assistance Button (Prominent for safety) */}
              <button
                onClick={() => setShowNeedAssistance(true)}
                className="mt-3 w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-red-50 text-red-700 font-medium hover:bg-red-100 transition-colors border border-red-200"
                data-testid="need-assistance-btn"
              >
                <AlertTriangle className="w-4 h-4" />
                Need Assistance
              </button>
            </div>
          </div>
        )}

        {/* Just Clocked Out - Show Next Visit */}
        {justClockedOut && !activeVisit && (
          <div className="space-y-4">
            <div className="bg-emerald-50 rounded-2xl p-4 border border-emerald-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <div className="font-semibold text-emerald-900">Visit Complete!</div>
                  <div className="text-sm text-emerald-700">Great work. Clock-out recorded.</div>
                </div>
              </div>
            </div>
            <NextVisitCard />
          </div>
        )}

        {/* Clock In Section (when not clocked in) */}
        {!activeVisit && !justClockedOut && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-testid="clock-in-section">
            <div className="px-4 py-3 border-b border-gray-100">
              <h2 className="font-semibold">Start Your Shift</h2>
              <p className="text-sm text-gray-500">Select a client to clock in</p>
            </div>
            
            {assignedClients.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <Users className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <div className="font-medium">No Assigned Clients</div>
                <div className="text-sm mt-1">Contact your supervisor for assignments.</div>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {assignedClients.map((client) => (
                  <div key={client.client_id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium">{client.client_name}</div>
                        {client.address && (
                          <div className="text-sm text-gray-500 mt-0.5 flex items-start gap-1">
                            <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                            <span className="line-clamp-2">{client.address}</span>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => handleClockIn(client.client_id)}
                        disabled={clockingIn}
                        className="ml-3 py-2.5 px-4 rounded-xl bg-[#06372B] text-white font-medium flex items-center gap-2 hover:bg-[#0a4a3a] transition-colors disabled:opacity-60 shrink-0"
                        data-testid={`clock-in-${client.client_id}`}
                      >
                        <Play className="w-4 h-4" />
                        {clockingIn ? "..." : "Clock In"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Today's Schedule */}
        {dashboard?.scheduled_shifts?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-testid="schedule-section">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <h2 className="font-semibold">Today&apos;s Schedule</h2>
              </div>
              <Link 
                to="/employee/schedule" 
                className="text-sm text-[#06372B] font-medium"
                data-testid="view-schedule-link"
              >
                View All
              </Link>
            </div>
            <div className="divide-y divide-gray-100">
              {dashboard.scheduled_shifts.map((shift) => (
                <div key={shift.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{shift.client_name}</div>
                      <div className="text-sm text-gray-500">
                        {shift.scheduled_start_time} - {shift.scheduled_end_time}
                      </div>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      shift.status === "completed" ? "bg-emerald-50 text-emerald-700" :
                      shift.status === "in_progress" ? "bg-blue-50 text-blue-700" :
                      "bg-gray-100 text-gray-600"
                    }`}>
                      {shift.status}
                    </span>
                  </div>
                  {/* Show Running Late for upcoming scheduled shifts */}
                  {["scheduled", "confirmed"].includes(shift.status) && (
                    <div className="mt-3 flex gap-2">
                      {shift.client_address && (
                        <button
                          onClick={() => openMaps(shift.client_address)}
                          className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-blue-50 text-blue-700 font-medium text-sm hover:bg-blue-100 transition-colors"
                          data-testid={`navigate-shift-${shift.id}`}
                        >
                          <Navigation className="w-4 h-4" />
                          Navigate
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setSelectedClient(shift);
                          setShowRunningLate(true);
                        }}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-amber-50 text-amber-700 font-medium text-sm hover:bg-amber-100 transition-colors"
                        data-testid={`running-late-shift-${shift.id}`}
                      >
                        <Clock className="w-4 h-4" />
                        Running Late
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Links */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <h2 className="font-semibold">Quick Links</h2>
          </div>
          <div className="divide-y divide-gray-100">
            <Link 
              to="/employee/schedule" 
              className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              data-testid="my-schedule-link"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#06372B]/10 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-[#06372B]" />
                </div>
                <div>
                  <div className="font-medium">My Schedule</div>
                  <div className="text-sm text-gray-500">Today, tomorrow, weekly</div>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </Link>
            
            <Link 
              to="/employee/clients" 
              className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              data-testid="my-clients-link"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="font-medium">My Clients</div>
                  <div className="text-sm text-gray-500">{assignedClients.length} assigned</div>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </Link>
            
            <Link 
              to="/employee/visits" 
              className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              data-testid="visit-history-link"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <div className="font-medium">Visit History</div>
                  <div className="text-sm text-gray-500">View past visits</div>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </Link>
            
            <Link 
              to="/employee/documents" 
              className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              data-testid="my-documents-link"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <div className="font-medium">My Documents</div>
                  <div className="text-sm text-gray-500">Certifications & files</div>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </Link>
            
            <Link 
              to="/employee/training" 
              className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              data-testid="training-link"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="font-medium">Training</div>
                  <div className="text-sm text-gray-500">Learning materials</div>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </Link>
          </div>
        </div>

        {/* Recent Visits */}
        {dashboard?.recent_visits?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-testid="recent-visits-section">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-semibold">Recent Visits</h2>
              <Link to="/employee/visits" className="text-sm text-[#06372B] font-medium">
                View All
              </Link>
            </div>
            <div className="divide-y divide-gray-100">
              {dashboard.recent_visits.slice(0, 5).map((visit) => (
                <Link 
                  key={visit.id} 
                  to={`/employee/visit/${visit.id}`}
                  className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <div>
                    <div className="font-medium">{visit.client_name}</div>
                    <div className="text-sm text-gray-500">
                      {new Date(visit.visit_date).toLocaleDateString()} • {visit.total_hours ? `${visit.total_hours}h` : "In progress"}
                    </div>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    visit.status === "approved" ? "bg-emerald-50 text-emerald-700" :
                    visit.status === "submitted" ? "bg-blue-50 text-blue-700" :
                    visit.status === "clocked_out" ? "bg-amber-50 text-amber-700" :
                    "bg-gray-100 text-gray-600"
                  }`}>
                    {visit.status.replace("_", " ")}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-2 z-40">
        <div className="max-w-lg mx-auto flex items-center justify-around">
          <Link 
            to="/employee/dashboard" 
            className="flex flex-col items-center gap-1 py-2 px-3 text-[#06372B]"
          >
            <Home className="w-5 h-5" />
            <span className="text-xs font-medium">Home</span>
          </Link>
          <Link 
            to="/employee/schedule" 
            className="flex flex-col items-center gap-1 py-2 px-3 text-gray-400 hover:text-[#06372B] transition-colors"
          >
            <Calendar className="w-5 h-5" />
            <span className="text-xs font-medium">Schedule</span>
          </Link>
          <Link 
            to="/employee/clients" 
            className="flex flex-col items-center gap-1 py-2 px-3 text-gray-400 hover:text-[#06372B] transition-colors"
          >
            <Users className="w-5 h-5" />
            <span className="text-xs font-medium">Clients</span>
          </Link>
          <Link 
            to="/employee/visits" 
            className="flex flex-col items-center gap-1 py-2 px-3 text-gray-400 hover:text-[#06372B] transition-colors"
          >
            <Clock className="w-5 h-5" />
            <span className="text-xs font-medium">Visits</span>
          </Link>
        </div>
      </nav>
      
      {/* Modals */}
      {showRunningLate && (
        <RunningLateModal
          visit={selectedClient || activeVisit}
          onClose={() => {
            setShowRunningLate(false);
            setSelectedClient(null);
          }}
          onSuccess={() => {
            loadDashboard();
          }}
        />
      )}
      
      {showNeedAssistance && (
        <NeedAssistanceModal
          visit={activeVisit}
          client={activeVisit?.client}
          onClose={() => setShowNeedAssistance(false)}
          onSuccess={() => {
            loadDashboard();
          }}
        />
      )}
      
      {showMessageOffice && (
        <MessageOfficeModal
          visit={activeVisit}
          client={activeVisit?.client}
          onClose={() => setShowMessageOffice(false)}
          onSuccess={() => {}}
        />
      )}
    </div>
  );
}
